import React, { useEffect, useRef, useState } from 'react';
import './experience.css';

const jobs = [
  {
    role: 'AI Engineer',
    company: 'Vision Systems & Intelligence Labs',
    location: 'Tucson, AZ',
    period: 'Jan 2025 – Jun 2026',
    type: 'Full-time',
    bullets: [
      'Built multimodal healthcare AI pipelines on 119,533 MIMIC-CXR records for radiology report generation and 14-label pathology classification using PyTorch, Hugging Face Transformers, BiomedCLIP, Meditron-7B, Clinical-T5, MedLLaMA, pandas, NumPy, and scikit-learn.',
      'Engineered clinical preprocessing with OpenCV CLAHE, schema validation, deduplication, label normalization, and patient-level train/val/test splits; built batched inference/evaluation utilities with structured outputs and JSON-style logging.',
      'Fine-tuned medical LLMs with PEFT/LoRA, 4-bit NF4 quantization, and CUDA/DDP; designed a CLS-conditioned cross-attention abstractor compressing 197 ViT tokens to 32 query tokens, training only 73M of 3.6B parameters.',
      'Developed class-imbalanced training/evaluation workflows using threshold tuning, AUROC, F1, BLEU-4, METEOR, and ROUGE-L; achieved 0.830 micro-AUROC, improved rare-class sensitivity by 70.88%, and delivered SPIE Medical Imaging accepted research.',
    ],
    stack: ['PyTorch', 'Hugging Face', 'BiomedCLIP', 'Meditron-7B', 'Clinical-T5', 'PEFT/LoRA', 'CUDA', 'OpenCV', 'scikit-learn'],
  },
  {
    role: 'Data Science Intern',
    company: 'Center for Railways Information System',
    location: 'New Delhi, India',
    period: 'May 2022 – Jul 2022',
    type: 'Internship',
    bullets: [
      'Designed and optimized ETL/backend systems over 1M+ railway records using Python, SQL, MySQL, pandas, NumPy, REST APIs, schema validation, query rewrites, composite indexing, and table partitioning, reducing query latency by 45% across real-time reporting and analytics workloads.',
      'Built ingestion APIs and automated batch workflows with reusable transformations, normalization checks, and cross-system reconciliation, eliminating 8 hrs/week of manual work and improving ML-ready data delivery across 3 systems.',
      'Containerized services with Docker and implemented GitHub Actions CI/CD, cutting deployment cycles from 2–3 weeks to under 2 days while improving reproducibility, release reliability, and engineering handoff.',
    ],
    stack: ['Python', 'MySQL', 'SQL', 'pandas', 'NumPy', 'Docker', 'GitHub Actions', 'REST APIs', 'Schema Design'],
  },
  {
    role: 'Data Science Intern',
    company: 'Softpro India Pvt Ltd',
    location: 'Lucknow, India',
    period: 'May 2021 – Jul 2021',
    type: 'Internship',
    bullets: [
      'Processed 1M+ ride-hailing records using Python, pandas, NumPy, SQL, scikit-learn, and XGBoost; built feature engineering, pricing prediction, and automated reporting workflows, reducing pricing variance by 20% and reporting turnaround from days to under 2 hours.',
    ],
    stack: ['Python', 'pandas', 'NumPy', 'SQL', 'scikit-learn', 'XGBoost', 'Feature Engineering'],
  },
];

const ExperienceCard = ({ job, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(index === 0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`expCard ${visible ? 'visible' : ''}`}
      style={{ animationDelay: `${index * 0.12}s` }}
    >
      <div className="expCardHeader" onClick={() => setExpanded(!expanded)}>
        <div className="expLeft">
          <div className="expRoleLine">
            <span className="expPrompt">~/experience $</span>
            <span className="expRole">{job.role}</span>
          </div>
          <div className="expMeta">
            <span className="expCompany">{job.company}</span>
            <span className="expDot">·</span>
            <span className="expLocation">{job.location}</span>
          </div>
        </div>
        <div className="expRight">
          <span className="expPeriod">{job.period}</span>
          <span className={`expType expType--${job.type === 'Internship' ? 'intern' : 'full'}`}>
            {job.type}
          </span>
          <span className={`expToggle ${expanded ? 'open' : ''}`}>▼</span>
        </div>
      </div>

      <div className={`expBody ${expanded ? 'expanded' : ''}`}>
        <ul className="expBullets">
          {job.bullets.map((b, i) => (
            <li key={i} className="expBullet">
              <span className="expBulletCaret">&gt;</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="expStack">
          {job.stack.map((s, i) => (
            <span key={i} className="expTag">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Experience = () => {
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHeaderVisible(true); },
      { threshold: 0.2 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="experience">
      <div className="expInner">
        <div ref={headerRef} className={`expHeader ${headerVisible ? 'visible' : ''}`}>
          <span className="expLabel">EXPERIENCE</span>
          <h2 className="expTitle">Where I've Worked</h2>
          <p className="expDesc">
            1+ years building AI systems, LLM pipelines, and data infrastructure across research, government, and industry.
          </p>
        </div>

        <div className="expTimeline">
          <div className="expLine"></div>
          {jobs.map((job, i) => (
            <ExperienceCard key={i} job={job} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;